import { AfterViewInit, Component, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { Subject } from 'rxjs';
import { AbstractControl, FormBuilder, FormGroupDirective, FormControlName, FormsModule, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { StateService } from '../../../@core/utils';
import { NbPopoverDirective, NbSelectComponent, NbCardModule, NbPopoverModule, NbInputModule, NbDatepickerModule, NbSelectModule, NbOptionModule, NbIconModule, NbButtonModule, NbSpinnerModule, NbDialogService, NbDialogRef } from '@nebular/theme';
import { RoleValidator } from '../../../utils/validators.validator';

import { getUserSchemaData } from './patient-FE-schema'
import { ApiService } from '../../../services/api/api.service';
import { entryProp } from '../../../interfaces/apiCalls';

import { filterRoleIdByName, NavigationHelpers } from '../../../common/utils';
import { environment } from '../../../../environments/environment';
import { TranslateService } from "@ngx-translate/core";
import { MatRippleModule } from '@angular/material/core';
import { WalletInfoCardComponent } from '../../dashboard/wallet-info-card/wallet-info-card.component';
import { NgIf, NgFor, NgSwitch, NgTemplateOutlet } from '@angular/common';

import { NbDateFnsDateModule } from '@nebular/date-fns';
import { NgLetModule } from 'ng-let';
import { CreatingComponent } from '../../../shared-classes/CreatingComponent';
import { StandardInputComponent } from "../../../shared-components/inputDataTypes/standard/standard-input.component";
import { OptionsInputComponent } from "../../../shared-components/inputDataTypes/options/options-input.component";
import { DateInputComponent } from '../../../shared-components/inputDataTypes/date/date-input.component';
import { DevicesService } from '../../../services/devices/devices.service';
import { AddressSearchComponent } from '../address-search/address-search.component';
import { Router } from '@angular/router';

@Component({
    selector: "app-create-patient",
    styleUrls: ["./create-patient.component.scss"],
    templateUrl: "./create-patient.component.html",
    standalone: true,
    imports: [NgIf, WalletInfoCardComponent, NbCardModule, FormsModule, ReactiveFormsModule, NgFor, NbPopoverModule, NgSwitch, NbInputModule, NbDatepickerModule, NbSelectModule, NbOptionModule, NbIconModule, NbButtonModule, MatRippleModule, NbSpinnerModule, NbDateFnsDateModule, NgLetModule, NgTemplateOutlet, StandardInputComponent, OptionsInputComponent, DateInputComponent, AddressSearchComponent]
})
export class CreatePatientComponent extends CreatingComponent implements OnInit, AfterViewInit {
  @ViewChildren(NbPopoverDirective) popovers: QueryList<NbPopoverDirective>;
  /* @ViewChildren(FormControlDirective) dateViews: QueryList<FormControlDirective>; */
  @ViewChildren(FormControlName) formControlName :FormControlName

  // the following is how to get a reference to the datepicker. all methods in its prototype can be called, even if typescript complains.
  // @ViewChild(NbDatepickerDirective) nbDatepickerDirective!: NbDatepickerDirective<string>;
  // @ViewChild('datepickerLeft', { static: false }) datepickerLeft!: NbDatepickerComponent<Date>;

  @ViewChild(FormGroupDirective) formGroupDirective!: FormGroupDirective;
  @ViewChild(NbSelectComponent) nbSelectComponent!: NbSelectComponent;

 // now get viewchild refs to templates, so they can be rendered by the template outlet based on their name.

  // all types of dates use the same template, as all the form validators etc. have 
  // already been set programatically in this controller.
  @ViewChild('dateAll', {static : true}) dateAfter:TemplateRef<any>
  @ViewChild('dateAll', {static : true}) dateBefore:TemplateRef<any>
  @ViewChild('dateAll', {static : true}) dateAny:TemplateRef<any>
  @ViewChild('options', {static : true}) options:TemplateRef<any>
  @ViewChild('defaultInputTemplate', {static : true}) defaultInputTemplate:TemplateRef<any>

  @ViewChild('createdDialog', { static: true }) createdDialog: TemplateRef<any>;
  
  // Built in the constructor (not as a field initializer) so subclasses can override
  // getSchema() — a subclass field initializer would run too late, after the base
  // constructor has already built the form from it.
  entry : entryProp[];

  get visiblePropCount() {
    return this.entry.filter(e => e.visible !== false).length;
  }

  /** Override to change which fields the form renders. */
  protected getSchema(): entryProp[] {
    return getUserSchemaData(this.translate);
  }

  /** Translation key for the card header. */
  titleKey = 'terms.createNewPatient';

  /** Translation key for the submit button. */
  submitLabelKey = 'dict.create';

  /* view-related start */
  environment = environment;
  isSingleView: boolean;
  actionSize = "medium";
  showSpinnerOnCreateButton = false;
  entryCreated; // the data of the user that was created

  private destroy$ = new Subject<void>();

  /* view-related end  */

  /* helpers start  */
  
  constructor(
    public state: StateService,
    fb: FormBuilder,
    public api: ApiService,
    public navigate: NavigationHelpers,
    public translate: TranslateService,
    public devices: DevicesService,
    protected dialogService: NbDialogService,
    protected router: Router
  ) 
  {
    super({fb, api, devices, dataType: "user", translate});

    this.entry = this.getSchema();

    // create the form:
    this.createForm(this.entryForm, this.fb, state, this.entry);
  }

  createForm = (formGroupRef : FormGroup, formBuilderRef : FormBuilder, stateRef : StateService, dataFields: entryProp[],  prefillFormWithValues = false) =>{

    // In this class, we handle only the name-based criteria (e.g. role), the rest (input type based, like "dateBefore") is done by base class.
    
    // first, let the base class add all validators by the input type
    super.createForm(formGroupRef, formBuilderRef, stateRef, dataFields, prefillFormWithValues);

    // the formgroup and the validators are defined now here, now add the specific validators for the different data fields
    for (const dataField of dataFields) {
      
      const existingFormControl = this.entryForm.get(dataField.name);
      if (!existingFormControl) {
        throw new Error("Form control not found for expected dataField: " + dataField.name);
      }

      //validators for medpoc-specific datatype (e.g. users) to add:
      const specificValidatorsToAdd = [];

      // handle twoFA
      if (dataField.name === "twoFA") {
        dataField.value = dataField.value.twoFAmethod;
      }

      // handle role
      if (dataField.name === "role" && !dataField.skipValidation) {
        specificValidatorsToAdd.push(RoleValidator.getRoleExistsValidator(stateRef.roles));
      }

      existingFormControl.addValidators(specificValidatorsToAdd);
   }
}

  setCorrectDate = super.setCorrectDate;
  
  /** Override to submit to a different endpoint. */
  protected submitEntry(patchedData: any): Promise<any> {
    return this.api.createNewUser(patchedData);
  }

  createNewUser() {

    let patchedData = this.patchFormData(this.entryForm.value);
    console.devlog("data", patchedData)
    this.showSpinnerOnCreateButton = true;
    this.submitEntry(patchedData)
      .then((result) => {
        this.showSpinnerOnCreateButton = false;
        this.entryCreated = result;
        const dialogRef = this.dialogService.open(this.createdDialog, {
          closeOnBackdropClick: false,
          closeOnEsc: false,
          autoFocus: false
        });
        setTimeout(() => {
          const homeButton: HTMLButtonElement | null =
            dialogRef.componentRef.location.nativeElement.querySelector('.dialog-primary-action');
          homeButton?.focus();
        });
      })
      .catch((error) => {
        this.showSpinnerOnCreateButton = false;
        console.devlog("Error creating user: ", error);
        this.handleApiError(error);
      });
  }

  patchFormData(data: any) {
    let patchedData = Object.assign({},data);
    
    // add 2FA method per default
    patchedData.twoFA = {
      twoFAmethod: "email",
    };

    // add the ID for the configured role
    patchedData.role = filterRoleIdByName(this.state.roles, patchedData.role);

    return patchedData;
  }

  goToHome(dialogRef: NbDialogRef<any>) {
    dialogRef.close();
    this.router.navigate(['/home']);
  }

  goToCreatedProfile(dialogRef: NbDialogRef<any>) {
    dialogRef.close();
    this.router.navigateByUrl(this.navigate.linkToProfileById(this.entryCreated.documentId));
  }

  createAnotherUser(dialogRef: NbDialogRef<any>) {
    console.devlog('being clicked')
    dialogRef.close();
    this.entryCreated = null;
    // resetForm (not form.reset()) also clears the "submitted" state,
    // so you don't get the red ng-invalid.ng-touched borders on the fresh form
    this.formGroupDirective.resetForm();
    setTimeout(() => this.entryForm.get('role')?.setValue('authenticated'));
  }

  invalidFieldLabels(): string {
    return Object.keys(this.entryForm.controls)
      .filter(name => this.entryForm.controls[name].invalid)
      .map(name => this.entry.find(f => f.name === name)?.label ?? name)
      .join(', ');
  }

  ngOnInit() {
    /* ATTENTION !  */
    this.setInputFieldWarningPopovers();
  }

  ngAfterViewInit(): void {
    
/*     console.log("formGroupDirective", this.formGroupDirective)
    console.log("formControlName", this.formControlName)
    console.log("entryForm", this.entryForm)
    console.log("nbDatepickerDirective", this.nbDatepickerDirective)
    console.log("nbSelectComponent", this.nbSelectComponent)
   */
    setTimeout(() => {
      let correctFormField: AbstractControl = this.entryForm.controls['role'];
      correctFormField.setValue("authenticated")
    });

  /*   setInterval(() => {
//@ts-ignore
      console.log(this.nbDatepickerDirective.hidePicker());
  }, 2000); */
  }

  ngOnDestroy() {
    //TODO: Store subscriptions from above, and unsubscribe in here.
  }
}

export const onNewGdtFile = (data: any) : Promise<boolean> => {
  return new Promise((resolve, reject) => {
    console.devlog("new gdt file", data);
    resolve(true);
  });
}

